import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import {
  GitBranch,
  Plus,
  Search,
  Edit2,
  Trash2,
  Loader2,
  Filter,
  Play,
  ToggleLeft,
  ToggleRight,
  Clock,
  Zap,
  Hand,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { formatDate } from "@ckgo2/shared/utils";
import toast from "react-hot-toast";
import clsx from "clsx";

interface Workflow {
  id: string;
  name: string;
  description?: string;
  triggerType: "status_change" | "time_based" | "manual";
  triggerConfig: unknown;
  actions: Array<{ type: string }>;
  isActive: boolean;
  priority: number;
  executionCount: number;
  lastExecutedAt?: string;
  lastError?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface WorkflowExecution {
  id: string;
  workflowId: string;
  workflowName?: string;
  orderId: string;
  triggerType: string;
  status: "pending" | "running" | "completed" | "failed";
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
}

const TRIGGER_TYPE_LABELS = {
  status_change: "Změna stavu",
  time_based: "Časový",
  manual: "Ruční",
};

const TRIGGER_TYPE_ICONS = {
  status_change: Zap,
  time_based: Clock,
  manual: Hand,
};

const TRIGGER_TYPE_COLORS = {
  status_change: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  time_based: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  manual: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
};

const ACTION_TYPE_LABELS: Record<string, string> = {
  send_email: "Email",
  generate_pdf: "PDF",
  slack_notification: "Slack",
  change_status: "Změna stavu",
  assign_guide: "Přiřazení průvodce",
  webhook: "Webhook",
};

const STATUS_COLORS = {
  pending: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  running: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const STATUS_ICONS = {
  pending: Clock,
  running: Loader2,
  completed: CheckCircle,
  failed: XCircle,
};

export default function WorkflowsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [triggerFilter, setTriggerFilter] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"workflows" | "executions">("workflows");

  // Fetch workflows
  const { data: workflowsData, isLoading: isLoadingWorkflows } = useQuery({
    queryKey: ["workflows", triggerFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (triggerFilter) params.set("trigger_type", triggerFilter);
      const query = params.toString();
      return api.get<ApiResponse<Workflow[]>>(`/api/admin/workflows${query ? `?${query}` : ""}`);
    },
  });

  // Fetch recent executions
  const { data: executionsData, isLoading: isLoadingExecutions } = useQuery({
    queryKey: ["workflow-executions-recent"],
    queryFn: () =>
      api.get<ApiResponse<WorkflowExecution[]>>("/api/admin/workflows/executions/recent"),
    enabled: activeTab === "executions",
  });

  const workflows = workflowsData?.data || [];
  const executions = executionsData?.data || [];

  // Filter workflows by search
  const filteredWorkflows = workflows.filter(
    (w) =>
      w.name.toLowerCase().includes(search.toLowerCase()) ||
      w.description?.toLowerCase().includes(search.toLowerCase())
  );

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: (id: string) =>
      api.patch<ApiResponse<{ isActive: boolean }>>(`/api/admin/workflows/${id}/toggle`),
    onSuccess: () => {
      toast.success("Stav workflow změněn");
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Nepodařilo se změnit stav workflow");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/workflows/${id}`),
    onSuccess: () => {
      toast.success("Workflow smazáno");
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Nepodařilo se smazat workflow");
    },
  });

  const handleDelete = (workflow: Workflow) => {
    if (window.confirm(`Opravdu chcete smazat workflow "${workflow.name}"?`)) {
      deleteMutation.mutate(workflow.id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Workflow
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Automatizace akcí při změnách objednávek
          </p>
        </div>
        <Link to="/workflows/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          Nové workflow
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab("workflows")}
          className={clsx(
            "border-b-2 px-4 py-2 text-sm font-medium transition-colors",
            activeTab === "workflows"
              ? "border-primary-500 text-primary-600 dark:text-primary-400"
              : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          )}
        >
          Workflow ({workflows.length})
        </button>
        <button
          onClick={() => setActiveTab("executions")}
          className={clsx(
            "border-b-2 px-4 py-2 text-sm font-medium transition-colors",
            activeTab === "executions"
              ? "border-primary-500 text-primary-600 dark:text-primary-400"
              : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          )}
        >
          Historie spuštění
        </button>
      </div>

      {activeTab === "workflows" && (
        <>
          {/* Filters */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Hledat workflow..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-10"
              />
            </div>

            {/* Trigger type filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={triggerFilter}
                onChange={(e) => setTriggerFilter(e.target.value)}
                className="input w-48"
              >
                <option value="">Všechny typy</option>
                <option value="status_change">Změna stavu</option>
                <option value="time_based">Časový</option>
                <option value="manual">Ruční</option>
              </select>
            </div>
          </div>

          {/* Workflows list */}
          {isLoadingWorkflows ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          ) : filteredWorkflows.length === 0 ? (
            <div className="card flex h-64 flex-col items-center justify-center">
              <GitBranch className="mb-4 h-12 w-12 text-gray-300" />
              <p className="text-gray-500">Žádná workflow</p>
              <Link to="/workflows/new" className="btn-primary mt-4">
                <Plus className="h-5 w-5" />
                Vytvořit první workflow
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredWorkflows.map((workflow) => {
                const TriggerIcon = TRIGGER_TYPE_ICONS[workflow.triggerType];

                return (
                  <div
                    key={workflow.id}
                    className={clsx(
                      "card group transition-shadow hover:shadow-lg",
                      !workflow.isActive && "opacity-60"
                    )}
                  >
                    <div className="flex items-start justify-between">
                      {/* Left side - info */}
                      <div className="flex items-start gap-4">
                        {/* Toggle */}
                        <button
                          onClick={() => toggleMutation.mutate(workflow.id)}
                          disabled={toggleMutation.isPending}
                          className="mt-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title={workflow.isActive ? "Deaktivovat" : "Aktivovat"}
                        >
                          {workflow.isActive ? (
                            <ToggleRight className="h-6 w-6 text-primary-500" />
                          ) : (
                            <ToggleLeft className="h-6 w-6" />
                          )}
                        </button>

                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-gray-900 dark:text-white">
                              {workflow.name}
                            </h3>
                            <span
                              className={clsx(
                                "inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium",
                                TRIGGER_TYPE_COLORS[workflow.triggerType]
                              )}
                            >
                              <TriggerIcon className="h-3 w-3" />
                              {TRIGGER_TYPE_LABELS[workflow.triggerType]}
                            </span>
                          </div>

                          {workflow.description && (
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                              {workflow.description}
                            </p>
                          )}

                          {/* Actions preview */}
                          <div className="mt-2 flex flex-wrap gap-1">
                            {workflow.actions.map((action, idx) => (
                              <span
                                key={idx}
                                className="inline-flex rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                              >
                                {ACTION_TYPE_LABELS[action.type] || action.type}
                              </span>
                            ))}
                          </div>

                          {/* Stats */}
                          <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                            <span>
                              Spuštěno: {workflow.executionCount}x
                            </span>
                            {workflow.lastExecutedAt && (
                              <span>
                                Naposledy: {formatDate(workflow.lastExecutedAt)}
                              </span>
                            )}
                            {workflow.lastError && (
                              <span className="flex items-center gap-1 text-error-600">
                                <AlertCircle className="h-3 w-3" />
                                Chyba
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right side - actions */}
                      <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <Link
                          to={`/workflows/${workflow.id}`}
                          className="btn-ghost btn-sm"
                          title="Upravit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(workflow)}
                          disabled={deleteMutation.isPending}
                          className="btn-ghost btn-sm text-error-600 hover:bg-error-50"
                          title="Smazat"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeTab === "executions" && (
        <>
          {isLoadingExecutions ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          ) : executions.length === 0 ? (
            <div className="card flex h-64 flex-col items-center justify-center">
              <Play className="mb-4 h-12 w-12 text-gray-300" />
              <p className="text-gray-500">Žádná spuštění</p>
            </div>
          ) : (
            <div className="card overflow-hidden p-0">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Workflow
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Objednávka
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Trigger
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Stav
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Čas
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                  {executions.map((execution) => {
                    const StatusIcon = STATUS_ICONS[execution.status];

                    return (
                      <tr key={execution.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {execution.workflowName || execution.workflowId}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <Link
                            to={`/orders/${execution.orderId}`}
                            className="text-primary-600 hover:underline"
                          >
                            {execution.orderId}
                          </Link>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span
                            className={clsx(
                              "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium",
                              TRIGGER_TYPE_COLORS[execution.triggerType as keyof typeof TRIGGER_TYPE_COLORS] || TRIGGER_TYPE_COLORS.manual
                            )}
                          >
                            {TRIGGER_TYPE_LABELS[execution.triggerType as keyof typeof TRIGGER_TYPE_LABELS] || execution.triggerType}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span
                            className={clsx(
                              "inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium",
                              STATUS_COLORS[execution.status]
                            )}
                          >
                            <StatusIcon className={clsx("h-3 w-3", execution.status === "running" && "animate-spin")} />
                            {execution.status}
                          </span>
                          {execution.errorMessage && (
                            <p className="mt-1 text-xs text-error-600">
                              {execution.errorMessage}
                            </p>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                          {formatDate(execution.createdAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
